import HelpOutlineIcon from "@mui/icons-material/HelpOutline"
import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { FC, useState } from "react"
import { useTranslation } from "react-i18next"
import { Configuration } from "shared/configuration"
import {
  DEFAULT_DEPOSIT_CODE_ZPL_TEMPLATE,
  DEPOSIT_CODE_AUTO_PRINT_CONFIG_REF_NAME,
  DEPOSIT_CODE_LABEL_OFFSET_X_CONFIG_REF_NAME,
  DEPOSIT_CODE_LABEL_OFFSET_Y_CONFIG_REF_NAME,
  DEPOSIT_CODE_ZPL_TEMPLATE_CONFIG_REF_NAME,
  DepositCodeZplVariableList,
} from "shared/depositCodePrintData"
import { useNotification } from "src/components/NotificationProvider"
import { Sl2Btn } from "src/components/Sl2Btn"
import { BrowserPrintSetupDialog } from "./BrowserPrintSetupDialog"
import { BrowserPrintStatusIcon } from "./BrowserPrintStatusIcon"
import { LabelOffsetFields } from "./LabelOffsetFields"
import { printErrorMessageKey } from "./printErrors"
import { useDepositCodePrinting } from "./useDepositCodePrinting"

type OsConfigurationPrintingPanelProps = {
  tenantId?: string
  configs: Configuration[]
  onChange: (config: Configuration) => void
}

/**
 * LOCKER-522 — the Configuration page's Printing tab: auto-print toggle, ZPL
 * override (empty = built-in default), label offsets, test print and the setup
 * guide.
 */
export const OsConfigurationPrintingPanel: FC<
  OsConfigurationPrintingPanelProps
> = ({ tenantId, configs, onChange }) => {
  const { t } = useTranslation()
  const { showNotification } = useNotification()
  const { health, printSample } = useDepositCodePrinting(tenantId)
  const [isHelpOpen, setIsHelpOpen] = useState(false)
  const [isPrinting, setIsPrinting] = useState(false)

  const autoPrint = configs.find(
    (c) => c.refName === DEPOSIT_CODE_AUTO_PRINT_CONFIG_REF_NAME,
  )
  const template = configs.find(
    (c) => c.refName === DEPOSIT_CODE_ZPL_TEMPLATE_CONFIG_REF_NAME,
  )
  const offsetX = configs.find(
    (c) => c.refName === DEPOSIT_CODE_LABEL_OFFSET_X_CONFIG_REF_NAME,
  )
  const offsetY = configs.find(
    (c) => c.refName === DEPOSIT_CODE_LABEL_OFFSET_Y_CONFIG_REF_NAME,
  )

  if (!autoPrint || !template) {
    // Rows come from tenant creation or seed9; neither has run for this tenant.
    return <Alert severity="warning">{t("printingConfigsMissing")}</Alert>
  }

  const isDefault = !template.sVal?.trim()

  const onTestPrint = async () => {
    setIsPrinting(true)
    try {
      // The unsaved edits, so a test label checks what Save will store.
      await printSample({
        template: template.sVal,
        offsetX: offsetX?.nVal,
        offsetY: offsetY?.nVal,
      })
      showNotification(t("browserPrintTestSent"), "success")
    } catch (err) {
      showNotification(t(printErrorMessageKey(err)), "error")
    } finally {
      setIsPrinting(false)
    }
  }

  return (
    <Stack spacing={3} sx={{ maxWidth: 1000 }} data-ui="UI-SL2W-293">
      <Stack direction="row" spacing={1} alignItems="center">
        <BrowserPrintStatusIcon health={health} />
        <Typography variant="body2">
          {health.status === "ready" && health.printer?.name
            ? t("browserPrintStatus.readyWithPrinter", {
                name: health.printer.name,
              })
            : t(`browserPrintStatus.${health.status}`)}
        </Typography>
        <Sl2Btn
          size="small"
          startIcon={<HelpOutlineIcon />}
          onClick={() => setIsHelpOpen(true)}
        >
          {t("browserPrintSetupTitle")}
        </Sl2Btn>
      </Stack>

      <Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={!!autoPrint.bVal}
              onChange={(e) =>
                onChange({ ...autoPrint, bVal: e.target.checked })
              }
            />
          }
          label={
            <Typography variant="body1" fontWeight="bold">
              {t(`${DEPOSIT_CODE_AUTO_PRINT_CONFIG_REF_NAME}.label`)}
            </Typography>
          }
        />
        <Typography variant="body2" color="text.secondary" sx={{ pl: 4 }}>
          {t(`${DEPOSIT_CODE_AUTO_PRINT_CONFIG_REF_NAME}.description`)}
        </Typography>
      </Box>

      <LabelOffsetFields
        offsetX={offsetX}
        offsetY={offsetY}
        onChange={onChange}
      />

      <Stack spacing={1}>
        <Typography variant="body1" fontWeight="bold">
          {t(`${DEPOSIT_CODE_ZPL_TEMPLATE_CONFIG_REF_NAME}.label`)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t(`${DEPOSIT_CODE_ZPL_TEMPLATE_CONFIG_REF_NAME}.description`)}
        </Typography>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <TextField
            multiline
            minRows={12}
            fullWidth
            value={template.sVal ?? ""}
            placeholder={DEFAULT_DEPOSIT_CODE_ZPL_TEMPLATE}
            onChange={(e) => onChange({ ...template, sVal: e.target.value })}
            helperText={
              isDefault ? t("zplTemplateUsingDefault") : t("zplTemplateCustom")
            }
            slotProps={{
              htmlInput: {
                spellCheck: false,
                style: { fontFamily: "monospace", fontSize: 13 },
              },
            }}
          />
          <Box sx={{ minWidth: 220 }}>
            <Typography variant="body2" fontWeight="bold">
              {t("availableVariables")}
            </Typography>
            {DepositCodeZplVariableList.map((v) => (
              <Box key={v} sx={{ mt: 1 }}>
                <Typography variant="body2" fontFamily="monospace">
                  {`{{${v}}}`}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t(`zplVariable.${v}`)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          {isDefault ? (
            <Sl2Btn
              variant="outlined"
              onClick={() =>
                onChange({
                  ...template,
                  sVal: DEFAULT_DEPOSIT_CODE_ZPL_TEMPLATE,
                })
              }
            >
              {t("zplTemplateStartFromDefault")}
            </Sl2Btn>
          ) : (
            <Sl2Btn
              variant="outlined"
              onClick={() => onChange({ ...template, sVal: "" })}
            >
              {t("zplTemplateRestoreDefault")}
            </Sl2Btn>
          )}
          <Sl2Btn
            variant="outlined"
            onClick={onTestPrint}
            disabled={isPrinting || health.status !== "ready"}
          >
            {t("browserPrintTestPrint")}
          </Sl2Btn>
        </Stack>
      </Stack>
      {isHelpOpen && (
        <BrowserPrintSetupDialog
          tenantId={tenantId}
          onClose={() => setIsHelpOpen(false)}
        />
      )}
    </Stack>
  )
}
